import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp, MapPin, Gift, Plus, Edit2, Pause, Play,
  Archive, Eye, CheckCircle2, Building2, Tag,
  ChevronDown, ChevronUp, Check, Info, Loader2, CreditCard, QrCode, Download,
  UserPlus, Phone, Mail, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { businessPageAccess } from '../../lib/roles';
import { businessApi, errorMessage } from '../../lib/api';
import { resolveAssetUrl, flag } from '../../config';
import ClaimsTable from '../../components/business/ClaimsTable';
import BusinessHeader from '../../components/business/BusinessHeader';
import ConfirmDialog from '../../components/business/ConfirmDialog';

const SUPPORT_EMAIL = 'hello@tappd.app';

// The dashboard caches its payload in sessionStorage; any write invalidates it.
const clearDashboardCache = () => {
  sessionStorage.removeItem('business_dashboard_data');
  sessionStorage.removeItem('business_dashboard_timestamp');
};

const BusinessDashboard = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [analytics, setAnalytics] = useState(null);
  const [deals, setDeals] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const [claimedExpanded, setClaimedExpanded] = useState(false);
  const [redeemedExpanded, setRedeemedExpanded] = useState(false);
  const [userBusinesses, setUserBusinesses] = useState([]);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrEnabled, setQrEnabled] = useState(true);
  
  // Leads state
  const [leads, setLeads] = useState([]);
  const [leadsStats, setLeadsStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0, closed: 0 });
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsFilter, setLeadsFilter] = useState('all');

  // Fetch all businesses for the user (multi-business is flagged off in Phase 1, PD-25)
  const fetchUserBusinesses = async () => {
    if (!flag('MULTI_BUSINESS')) return;
    try {
      const response = await businessApi.get('/business/my-businesses');
      setUserBusinesses(response.data.businesses || []);
    } catch (error) {
      console.error('Error fetching user businesses:', error);
    }
  };

  // Switch to a different business
  const handleSwitchBusiness = async (businessId) => {
    try {
      await businessApi.post(`/business/switch/${businessId}`, {});
      // Reload the page to refresh all data for the new business
      window.location.reload();
    } catch (error) {
      console.error('Error switching business:', error);
    }
  };

  useEffect(() => {
    // Wait for auth context to finish loading
    if (authLoading) return;

    // Check if user has business access (role logic: lib/roles.js businessPageAccess)
    const hasBusinessAccess = businessPageAccess(user);

    // Auth has finished loading
    if (!isAuthenticated || !hasBusinessAccess) {
      setAccessChecked(true);
      setHasAccess(false);
      return;
    }

    // User is authenticated and has business access
    setAccessChecked(true);
    setHasAccess(true);
    checkOnboarding();
  }, [isAuthenticated, authLoading, user, location.pathname]);

  // Refetch data when returning to dashboard from other pages (not on initial mount)
  useEffect(() => {
    if (!hasAccess || authLoading || !isAuthenticated) return;

    // Check if user has business access
    const hasBusinessAccess = businessPageAccess(user);
    if (!hasBusinessAccess) return;

    // Only refetch if we have stale cached data (older than 30 seconds)
    const cachedTimestamp = sessionStorage.getItem('business_dashboard_timestamp');
    const now = Date.now();
    if (cachedTimestamp && (now - parseInt(cachedTimestamp)) < 30000) return;

    fetchData();
  }, [hasAccess, authLoading, isAuthenticated, user?.account_type, user?.role, user?.business_id]);


  // Note: Removed window focus refetch to prevent unnecessary reloads
  // Data will be refreshed via cache staleness check or manual refresh


  const checkOnboarding = async () => {
    try {
      const onboardingRes = await businessApi.get('/business/onboarding-status');
      
      if (!onboardingRes.data.completed) {
        navigate('/business/onboarding');
        return;
      }
      
      fetchData();
      fetchUserBusinesses(); // Also fetch all user businesses
    } catch (error) {
      console.error('Error checking onboarding:', error);
      fetchData(); // Continue anyway if check fails
      fetchUserBusinesses();
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      // Try to load cached data first for instant display
      const cachedData = sessionStorage.getItem('business_dashboard_data');
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          setAnalytics(parsed.analytics);
          setDeals(parsed.deals);
          setSubscription(parsed.subscription);
          setLocations(parsed.locations);
          setBusinessInfo(parsed.businessInfo);
          setAccountStatus(parsed.accountStatus);
        } catch (e) {
          console.error('Error parsing cached data:', e);
        }
      }

      const [analyticsRes, dealsRes, subRes, locationsRes, profileRes, statusRes] = await Promise.all([
        businessApi.get('/business/analytics/overview'),
        businessApi.get('/business/deals'),
        businessApi.get('/business/subscription'),
        businessApi.get('/business/locations'),
        businessApi.get('/business/profile'),
        businessApi.get('/business/account-status').catch(() => ({ data: {} }))
      ]);

      const analyticsData = analyticsRes.data;
      const dealsData = dealsRes.data;
      const subscriptionData = subRes.data;
      const locationsData = locationsRes.data?.locations || [];
      const businessInfoData = {
        ...profileRes.data,
        business_name: profileRes.data.name
      };
      const accountStatusData = statusRes.data;

      setAnalytics(analyticsData);
      setDeals(dealsData);
      setSubscription(subscriptionData);
      setLocations(locationsData);
      setBusinessInfo(businessInfoData);
      setAccountStatus(accountStatusData);
      
      // Set QR code URL if exists
      if (businessInfoData.instore_qr_code_url) {
        setQrCodeUrl(businessInfoData.instore_qr_code_url);
        setQrEnabled(businessInfoData.instore_qr_enabled !== false);
      }

      // Cache the data for next navigation with timestamp
      sessionStorage.setItem('business_dashboard_data', JSON.stringify({
        analytics: analyticsData,
        deals: dealsData,
        subscription: subscriptionData,
        locations: locationsData,
        businessInfo: businessInfoData,
        accountStatus: accountStatusData
      }));
      sessionStorage.setItem('business_dashboard_timestamp', Date.now().toString());

      // Also fetch leads data
      fetchLeads();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch leads for the business
  const fetchLeads = async () => {
    try {
      setLeadsLoading(true);

      const [leadsRes, statsRes] = await Promise.all([
        businessApi.get('/business/leads'),
        businessApi.get('/business/leads/stats')
      ]);
      
      setLeads(leadsRes.data || []);
      setLeadsStats(statsRes.data || { total: 0, new: 0, contacted: 0, converted: 0, closed: 0 });
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLeadsLoading(false);
    }
  };
  
  // Update lead status
  const handleUpdateLeadStatus = async (leadId, newStatus, notes = null) => {
    try {
      await businessApi.patch(`/business/leads/${leadId}/status`, { status: newStatus, notes });
      fetchLeads(); // Refresh leads
    } catch (error) {
      console.error('Error updating lead status:', error);
      toast.error(errorMessage(error, 'Could not update this lead.'));
    }
  };

  const handleTogglePause = async (dealId, isPaused) => {
    try {
      await businessApi.patch(`/business/deals/${dealId}/pause?is_paused=${!isPaused}`, {});
      clearDashboardCache();
      fetchData();
      toast.success(isPaused ? 'Deal resumed.' : 'Deal paused.');
    } catch (error) {
      console.error('Error toggling pause:', error);
      toast.error(errorMessage(error, 'Could not update this deal.'));
    }
  };

  // Generate or regenerate QR code for in-store signage
  const handleGenerateQR = async () => {
    setQrLoading(true);
    try {
      const response = await businessApi.post('/business/generate-instore-qr', {});
      if (response.data.success) {
        // Add cache-busting timestamp to force image reload
        const newUrl = `${response.data.qr_code_url}?t=${Date.now()}`;
        setQrCodeUrl(newUrl);
        setQrEnabled(true);
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error(errorMessage(error, 'Could not generate the QR code.'));
    } finally {
      setQrLoading(false);
    }
  };

  // Download QR code image
  const handleDownloadQR = () => {
    if (!qrCodeUrl) return;
    const link = document.createElement('a');
    // Remove cache-busting parameter for download
    const cleanUrl = qrCodeUrl.split('?')[0];
    link.href = resolveAssetUrl(cleanUrl);
    link.download = `${businessInfo?.slug || 'business'}-instore-qr.png`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Archive asks for confirmation (it hides a live deal); restore does not — the same
  // rule as the deals page (audit rows 55 and 73).
  const [dealToArchive, setDealToArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);

  const handleArchive = async () => {
    if (!dealToArchive) return;
    setArchiving(true);
    try {
      await businessApi.delete(`/business/deals/${dealToArchive.id}`);
      clearDashboardCache();
      setDealToArchive(null);
      fetchData();
      toast.success('Deal archived.');
    } catch (error) {
      console.error('Error archiving deal:', error);
      toast.error(errorMessage(error, 'Failed to archive this deal.'));
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async (dealId) => {
    try {
      await businessApi.patch(`/business/deals/${dealId}/restore`, {});
      clearDashboardCache();
      fetchData();
      toast.success('Deal restored.');
    } catch (error) {
      console.error('Error restoring deal:', error);
      toast.error(errorMessage(error, 'Failed to restore this deal.'));
    }
  };

  const handleEditBusiness = () => {
    // Navigate to the Business Details edit page
    navigate('/business/settings');
  };

  if (authLoading || loading || !accessChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }}>
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#8b5cf6] border-t-transparent"></div>
        </div>
      </div>
    );
  }

  // If access check completed but user doesn't have access
  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFF6EA' }}>
        <div className="text-center max-w-md px-4">
          <Building2 size={64} className="mx-auto mb-4 text-orange-400" />
          <h1 className="font-display text-2xl text-gray-900 mb-2">
            Business Dashboard Access Required
          </h1>
          <p className="text-gray-600 mb-6">
            You need to be registered as a business advertiser to access this dashboard.
          </p>
          <Button
            onClick={() => navigate('/pricing')}
            className="rounded-xl px-8 py-3"
            style={{ backgroundColor: '#FF7A1A', color: '#FFFFFF' }}
          >
            Become an Advertiser
          </Button>
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="ml-4 rounded-xl px-8 py-3"
          >
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  // Check if business is pending verification
  // Phase 1: a business that has not paid yet must see the dashboard (with the
  // "Choose a plan" banner), not the pending-review screen — otherwise anyone who
  // abandons Stripe Checkout has no way back to pay.
  const isPendingPayment = accountStatus?.subscription_status === 'pending_payment';
  const isPendingVerification = accountStatus?.verification_status === 'pending' && !isPendingPayment;
  const isRejected = accountStatus?.verification_status === 'rejected';

  if (isPendingVerification) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFF6EA' }}>
        <div className="text-center max-w-lg px-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
          >
            <div className="w-20 h-20 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="font-display text-2xl text-gray-900 mb-4">
              Account Pending Approval
            </h1>
            <p className="text-gray-600 mb-6">
              Your business account is currently being reviewed by our team. You'll receive an email once your account is approved.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-amber-800">
                <strong>What's next?</strong> A Tappd admin will review your business details and approve your account, usually within 24 hours.
              </p>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Questions? Contact us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-orange-600 hover:underline">{SUPPORT_EMAIL}</a>
            </p>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="rounded-full px-8 py-3"
            >
              Back to Home
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  if (isRejected) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFF6EA' }}>
        <div className="text-center max-w-lg px-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
          >
            <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="font-display text-2xl text-gray-900 mb-4">
              Application Not Approved
            </h1>
            <p className="text-gray-600 mb-4">
              Unfortunately, your business application was not approved at this time.
            </p>
            {accountStatus?.verification_note && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-left">
                <p className="text-sm font-semibold text-red-800 mb-1">Reason:</p>
                <p className="text-sm text-red-700">{accountStatus.verification_note}</p>
              </div>
            )}
            <p className="text-sm text-gray-500 mb-6">
              If you have questions or believe this was a mistake, please contact us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-orange-600 hover:underline">{SUPPORT_EMAIL}</a>
            </p>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="rounded-full px-8 py-3"
            >
              Back to Home
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Share of claimed deals that were actually redeemed. Named for what it measures —
  // the API's own `conversion_rate` is claims/views, a different number (audit row 58).
  const redemptionRate = analytics?.total_claims > 0
    ? `${((analytics.total_redemptions / analytics.total_claims) * 100).toFixed(1)}%`
    : '0%';

  const activeDeals = deals.filter(d => !d.archived && d.is_active);
  const archivedDeals = deals.filter(d => d.archived);
  const displayDeals = showArchived ? archivedDeals : activeDeals;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FEF7ED' }}>
      {/* V5 Business Header */}
      <BusinessHeader 
        user={user}
        userName={`${user?.first_name || ''} ${user?.last_name || ''}`}
        currentBusiness={businessInfo}
        businesses={userBusinesses}
        onSwitchBusiness={handleSwitchBusiness}
      />
      
      {/* Main Content */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
        
        {/* Business Title + Plan Badge */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="font-mono text-xs mb-2" style={{ color: '#8b5cf6' }}>// dashboard_overview</p>
              <h1 className="font-display text-[32px] text-[#1e1b4b]">
                {businessInfo?.business_name || businessInfo?.name || analytics?.business_name || 'My Business'}
              </h1>
              <p className="text-[#1e1b4b]/50 mt-2">
                Manage your business, deals, and subscription
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Plan Badge */}
              <span 
                className="px-4 py-2 bg-gradient-to-r from-[#8b5cf6] to-[#7c3aed] text-white font-bold rounded-full text-sm"
                style={{ boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2)' }}
              >
                {accountStatus?.plan_name || subscription?.plan_tier || 'Free'} Plan
              </span>
              <span className="text-[#1e1b4b]/60 text-sm">
                {subscription?.deals_used || 0}/{subscription?.deal_limit || 1} Deals
              </span>
            </div>
          </div>
        </div>

        {/* Payment pending banner (Phase 1: no trial — subscribe to go live) */}
        {accountStatus?.subscription_status === 'pending_payment' && (
          <div className="mb-8 p-4 rounded-2xl border border-amber-200" style={{ background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1), rgba(249, 115, 22, 0.1))' }}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="font-semibold text-[#1e1b4b]">Your listing isn't live yet</p>
                <p className="text-[#1e1b4b]/60 text-sm">Choose a plan to finish setup. Your deal goes live once payment and review are complete.</p>
              </div>
              <a href="/business/subscription" className="px-5 py-2.5 bg-gradient-to-r from-[#8b5cf6] to-[#7c3aed] text-white rounded-xl font-semibold hover:shadow-lg transition-all">
                Choose a plan
              </a>
            </div>
          </div>
        )}
        {accountStatus?.subscription_status === 'expired' && (
          <div className="mb-8 p-4 rounded-2xl border border-amber-200" style={{ background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1), rgba(249, 115, 22, 0.1))' }}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="font-semibold text-[#1e1b4b]">Your plan has ended</p>
                <p className="text-[#1e1b4b]/60 text-sm">Your deals are paused. Choose a plan to put them back in front of patrons.</p>
              </div>
              <a href="/business/subscription" className="px-5 py-2.5 bg-gradient-to-r from-[#8b5cf6] to-[#7c3aed] text-white rounded-xl font-semibold hover:shadow-lg transition-all">
                Choose a plan
              </a>
            </div>
          </div>
        )}
        {accountStatus?.subscription_status === 'past_due' && (
          <div className="mb-8 p-4 rounded-2xl border border-red-200 bg-red-50">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="font-semibold text-[#1e1b4b]">Payment failed</p>
                <p className="text-[#1e1b4b]/60 text-sm">Update your card in the billing portal to keep your deal live.</p>
              </div>
              <a href="/business/subscription-manage" className="px-5 py-2.5 bg-red-600 text-white rounded-xl font-semibold">Update billing</a>
            </div>
          </div>
        )}

        {/* Stats Grid - 2x2 */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* Views Card - Purple */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-4 md:p-5"
            style={{ 
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #8b5cf6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)' 
            }}
          >
            <div className="flex items-center gap-2 md:gap-3 md:mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
                <Eye className="w-5 h-5 text-[#8b5cf6]" />
              </div>
              <p className="text-2xl md:hidden font-bold text-[#1e1b4b]">{analytics?.total_views || 0}</p>
              <span className="hidden md:inline text-[13px] font-medium" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Views</span>
            </div>
            <span className="md:hidden text-[11px] font-medium mt-2 block" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Views</span>
            <p className="hidden md:block text-[32px] font-bold text-[#1e1b4b]">{analytics?.total_views || 0}</p>
          </motion.div>

          {/* Claims Card - Orange */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white rounded-xl p-4 md:p-5"
            style={{ 
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #f97316',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)' 
            }}
          >
            <div className="flex items-center gap-2 md:gap-3 md:mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(249, 115, 22, 0.1)' }}>
                <Gift className="w-5 h-5 text-[#f97316]" />
              </div>
              <p className="text-2xl md:hidden font-bold text-[#1e1b4b]">{analytics?.total_claims || 0}</p>
              <span className="hidden md:inline text-[13px] font-medium" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Claims</span>
            </div>
            <span className="md:hidden text-[11px] font-medium mt-2 block" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Claims</span>
            <p className="hidden md:block text-[32px] font-bold text-[#1e1b4b]">{analytics?.total_claims || 0}</p>
          </motion.div>

          {/* Redemptions Card - Teal */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-4 md:p-5"
            style={{ 
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #14b8a6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)' 
            }}
          >
            <div className="flex items-center gap-2 md:gap-3 md:mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)' }}>
                <CheckCircle2 className="w-5 h-5 text-[#14b8a6]" />
              </div>
              <p className="text-2xl md:hidden font-bold text-[#1e1b4b]">{analytics?.total_redemptions || 0}</p>
              <span className="hidden md:inline text-[13px] font-medium" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Redemptions</span>
            </div>
            <span className="md:hidden text-[11px] font-medium mt-2 block" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Redemptions</span>
            <p className="hidden md:block text-[32px] font-bold text-[#1e1b4b]">{analytics?.total_redemptions || 0}</p>
          </motion.div>

          {/* Conversion Card - Green */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-xl p-4 md:p-5"
            style={{ 
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #22c55e',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)' 
            }}
          >
            <div className="flex items-center gap-2 md:gap-3 md:mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
                <TrendingUp className="w-5 h-5 text-[#22c55e]" />
              </div>
              <p className="text-2xl md:hidden font-bold text-[#1e1b4b]">{redemptionRate}</p>
              <span className="hidden md:inline text-[13px] font-medium" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Redemption rate</span>
            </div>
            <span className="md:hidden text-[11px] font-medium mt-2 block" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Redemption rate</span>
            <p className="hidden md:block text-[32px] font-bold text-[#1e1b4b]">{redemptionRate}</p>
          </motion.div>
        </div>

        {/* Two Column Layout: Business Info + Subscription */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Business Information Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl overflow-hidden"
            style={{ 
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #8b5cf6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)' 
            }}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
                    <Building2 className="w-5 h-5 text-[#8b5cf6]" />
                  </div>
                  <div>
                    <h2 className="text-[18px] font-semibold text-[#1e1b4b]">Business Info</h2>
                    <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>{businessInfo?.category || 'Category'}</p>
                  </div>
                </div>
                <button
                  onClick={handleEditBusiness}
                  className="px-4 py-2 bg-white text-[#8b5cf6] font-semibold rounded-xl flex items-center gap-2 transition-all duration-200 hover:-translate-y-0.5"
                  style={{ 
                    border: '2px solid rgba(139, 92, 246, 0.3)',
                    boxShadow: '0 2px 8px rgba(139, 92, 246, 0.1)' 
                  }}
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
              </div>
              
              {/* Banner Image */}
              {businessInfo?.image_url && (
                <div className="mb-4 rounded-xl overflow-hidden">
                  <img 
                    src={resolveAssetUrl(businessInfo.image_url)} 
                    alt={businessInfo.name}
                    className="w-full h-32 object-cover"
                  />
                </div>
              )}
              
              <div className="space-y-3">
                {businessInfo?.description && (
                  <p className="text-[14px] line-clamp-2" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>{businessInfo.description}</p>
                )}
                {businessInfo?.address && (
                  <div className="flex items-start gap-2 text-[14px]">
                    <MapPin className="w-4 h-4 mt-0.5" style={{ color: 'rgba(30, 27, 75, 0.4)' }} />
                    <span style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                      {businessInfo.address}, {businessInfo.city}, {businessInfo.state}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Subscription Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-white rounded-xl overflow-hidden"
            style={{ 
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #14b8a6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)' 
            }}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)' }}>
                    <CreditCard className="w-5 h-5 text-[#14b8a6]" />
                  </div>
                  <div>
                    <h2 className="text-[18px] font-semibold text-[#1e1b4b]">Subscription</h2>
                    <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Plan & Usage</p>
                  </div>
                </div>
                <span 
                  className="px-4 py-1.5 text-white font-bold text-[14px] rounded-full"
                  style={{ 
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    boxShadow: '0 2px 10px rgba(34, 197, 94, 0.4)' 
                  }}
                >
                  {accountStatus?.plan_name || subscription?.plan_tier || 'Free'}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(30, 27, 75, 0.03)' }}>
                  <p className="text-[12px] font-medium mb-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Active Deals</p>
                  <p className="text-xl font-bold text-[#1e1b4b]">
                    {subscription?.deals_used || 0}/{subscription?.deal_limit || 1}
                  </p>
                </div>
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(30, 27, 75, 0.03)' }}>
                  <p className="text-[12px] font-medium mb-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Partner venues</p>
                  <p className="text-xl font-bold text-[#1e1b4b]">
                    {locations?.length || 0}/{typeof subscription?.location_limit === 'number' ? subscription.location_limit : (subscription?.location_limit || 1)}
                  </p>
                </div>
              </div>
              
              <Link
                to="/business/subscription-manage"
                className="w-full py-3 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all duration-200 hover:-translate-y-0.5"
                style={{ 
                  background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                  boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2)' 
                }}
              >
                Manage Subscription
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Expandable: Claimed Offers */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl mb-6 overflow-hidden"
          style={{ 
            border: '1px solid rgba(30, 27, 75, 0.05)',
            borderLeft: '4px solid #f97316',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)' 
          }}
        >
          <button 
            onClick={() => setClaimedExpanded(!claimedExpanded)}
            className="w-full px-6 py-4 flex items-center justify-between transition-colors"
            style={{ backgroundColor: claimedExpanded ? 'rgba(30, 27, 75, 0.02)' : 'transparent' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(249, 115, 22, 0.1)' }}>
                <Gift className="w-5 h-5 text-[#f97316]" />
              </div>
              <div className="text-left">
                <h3 className="text-[18px] font-semibold text-[#1e1b4b]">Claimed Offers</h3>
                <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Active claims pending redemption</p>
              </div>
            </div>
            {claimedExpanded ? (
              <ChevronUp className="w-5 h-5" style={{ color: 'rgba(30, 27, 75, 0.3)' }} />
            ) : (
              <ChevronDown className="w-5 h-5" style={{ color: 'rgba(30, 27, 75, 0.3)' }} />
            )}
          </button>
          <AnimatePresence>
            {claimedExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                style={{ borderTop: '1px solid rgba(30, 27, 75, 0.05)' }}
              >
                <div className="p-6">
                  <ClaimsTable type="claimed" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Expandable: Redeemed Offers */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="bg-white rounded-xl mb-6 overflow-hidden"
          style={{ 
            border: '1px solid rgba(30, 27, 75, 0.05)',
            borderLeft: '4px solid #22c55e',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)' 
          }}
        >
          <button 
            onClick={() => setRedeemedExpanded(!redeemedExpanded)}
            className="w-full px-6 py-4 flex items-center justify-between transition-colors"
            style={{ backgroundColor: redeemedExpanded ? 'rgba(30, 27, 75, 0.02)' : 'transparent' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
                <CheckCircle2 className="w-5 h-5 text-[#22c55e]" />
              </div>
              <div className="text-left">
                <h3 className="text-[18px] font-semibold text-[#1e1b4b]">Redeemed Offers</h3>
                <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Successfully redeemed claims</p>
              </div>
            </div>
            {redeemedExpanded ? (
              <ChevronUp className="w-5 h-5" style={{ color: 'rgba(30, 27, 75, 0.3)' }} />
            ) : (
              <ChevronDown className="w-5 h-5" style={{ color: 'rgba(30, 27, 75, 0.3)' }} />
            )}
          </button>
          <AnimatePresence>
            {redeemedExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                style={{ borderTop: '1px solid rgba(30, 27, 75, 0.05)' }}
              >
                <div className="p-6">
                  <ClaimsTable type="redeemed" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {flag('INSTANT_REDEEM') && (<>
        {/* In-Store QR Code Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="bg-white rounded-xl p-6 mb-6"
          style={{ 
            border: '1px solid rgba(30, 27, 75, 0.05)',
            borderLeft: '4px solid #8b5cf6',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)' 
          }}
        >
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            {/* Left side - Info */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
                <QrCode className="w-6 h-6 text-[#8b5cf6]" />
              </div>
              <div>
                <h3 className="text-[18px] font-semibold text-[#1e1b4b] mb-1">In-Store QR Code</h3>
                <p className="text-[13px] mb-3" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                  Print this QR code for your door or window signage. When customers scan it, they&apos;ll instantly claim and redeem your deal.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {qrCodeUrl ? (
                    <>
                      <Button
                        onClick={handleDownloadQR}
                        className="rounded-xl text-[13px] font-semibold"
                        style={{
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                          color: 'white',
                          boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)'
                        }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download QR
                      </Button>
                      <Button
                        onClick={handleGenerateQR}
                        variant="outline"
                        className="rounded-xl text-[13px] font-semibold"
                        style={{ borderColor: 'rgba(30, 27, 75, 0.1)', color: 'rgba(30, 27, 75, 0.7)' }}
                        disabled={qrLoading}
                      >
                        {qrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Regenerate'}
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={handleGenerateQR}
                      className="rounded-xl text-[13px] font-semibold"
                      style={{
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        color: 'white',
                        boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)'
                      }}
                      disabled={qrLoading}
                    >
                      {qrLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <QrCode className="w-4 h-4 mr-2" />
                          Generate QR Code
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Right side - QR Preview */}
            {qrCodeUrl && (
              <div className="flex-shrink-0">
                <div 
                  className="w-32 h-32 bg-white rounded-xl flex items-center justify-center overflow-hidden"
                  style={{ border: '2px solid rgba(30, 27, 75, 0.1)' }}
                >
                  <img 
                    src={resolveAssetUrl(qrCodeUrl)}
                    alt="In-Store QR Code"
                    className="w-full h-full object-contain p-2"
                    onError={(e) => {
                      console.error('QR image failed to load:', e.target.src);
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="text-xs text-center text-gray-400 p-2">QR image not found.<br/>Click Regenerate.</div>';
                    }}
                  />
                </div>
                <p className="text-center text-[11px] mt-2" style={{ color: 'rgba(30, 27, 75, 0.4)' }}>
                  Preview
                </p>
              </div>
            )}
          </div>
          
          {/* How it works */}
          {qrCodeUrl && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'rgba(30, 27, 75, 0.05)' }}>
              <p className="text-[12px] font-semibold text-[#1e1b4b] mb-2">How it works:</p>
              <div className="flex flex-wrap gap-4 text-[12px]" style={{ color: 'rgba(30, 27, 75, 0.6)' }}>
                <span className="flex items-center gap-1">
                  <span className="w-5 h-5 rounded-full bg-[rgba(139,92,246,0.1)] text-[#8b5cf6] flex items-center justify-center text-[10px] font-bold">1</span>
                  Customer scans QR
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-5 h-5 rounded-full bg-[rgba(139,92,246,0.1)] text-[#8b5cf6] flex items-center justify-center text-[10px] font-bold">2</span>
                  Logs in or signs up
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-5 h-5 rounded-full bg-[rgba(139,92,246,0.1)] text-[#8b5cf6] flex items-center justify-center text-[10px] font-bold">3</span>
                  Deal auto-redeems
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-5 h-5 rounded-full bg-[rgba(139,92,246,0.1)] text-[#8b5cf6] flex items-center justify-center text-[10px] font-bold">4</span>
                  Shows code to staff
                </span>
              </div>
            </div>
          )}
        </motion.div>

        </>)}

        {/* Your Deals Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl p-6 mb-6"
          style={{ 
            border: '1px solid rgba(30, 27, 75, 0.05)',
            borderLeft: '4px solid #8b5cf6',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)' 
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
                <Tag className="w-5 h-5 text-[#8b5cf6]" />
              </div>
              <div>
                <h2 className="text-[18px] font-semibold text-[#1e1b4b]">Your Deals</h2>
                <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Manage your active offers</p>
              </div>
            </div>
            
            {/* Tabs - Purple active state per V5 Design Language */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                onClick={() => setShowArchived(false)}
                className={`px-4 py-2 rounded-full text-[14px] font-semibold transition-all duration-200 ${
                  !showArchived 
                    ? 'text-white' 
                    : 'hover:bg-[rgba(30,27,75,0.05)]'
                }`}
                style={!showArchived ? { 
                  backgroundColor: '#8b5cf6',
                  boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2)' 
                } : { color: 'rgba(30, 27, 75, 0.5)' }}
              >
                Active ({activeDeals.length})
              </button>
              <button
                onClick={() => setShowArchived(true)}
                className={`px-4 py-2 rounded-full text-[14px] font-semibold transition-all duration-200 ${
                  showArchived 
                    ? 'text-white' 
                    : 'hover:bg-[rgba(30,27,75,0.05)]'
                }`}
                style={showArchived ? { 
                  backgroundColor: '#8b5cf6',
                  boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2)' 
                } : { color: 'rgba(30, 27, 75, 0.5)' }}
              >
                Archived ({archivedDeals.length})
              </button>
            </div>
          </div>
          
          {/* New Deal Button - Dashed style per V5; disabled at the plan's deal cap */}
          <button
            disabled={(subscription?.deals_used ?? 0) >= (subscription?.deal_limit ?? 1)}
            title={(subscription?.deals_used ?? 0) >= (subscription?.deal_limit ?? 1) ? `Your plan allows ${subscription?.deal_limit ?? 1} active deal${(subscription?.deal_limit ?? 1) === 1 ? '' : 's'}. Archive one or upgrade to add more.` : undefined}
            onClick={() => navigate('/business/deals/new')}
            className="w-full py-3 rounded-xl text-[#8b5cf6] font-semibold flex items-center justify-center gap-2 transition-all duration-200 hover:bg-[rgba(139,92,246,0.05)] mb-6"
            style={{ border: '2px dashed rgba(139, 92, 246, 0.3)' }}
          >
            <Plus className="w-5 h-5" />
            {(subscription?.deals_used ?? 0) >= (subscription?.deal_limit ?? 1) ? `Deal limit reached (${subscription?.deals_used}/${subscription?.deal_limit})` : `New Deal (${subscription?.deals_used ?? 0}/${subscription?.deal_limit ?? 1} used)`}
          </button>
          
          {/* Deal Cards */}
          <div className="space-y-4">
            {displayDeals.length === 0 ? (
              <div className="text-center py-8" style={{ color: 'rgba(30, 27, 75, 0.4)' }}>
                {showArchived ? 'No archived deals' : 'No active deals yet. Create your first deal!'}
              </div>
            ) : (
              displayDeals.map((deal) => (
                <div 
                  key={deal.id}
                  className="p-5 rounded-xl transition-all duration-200 hover:-translate-y-0.5"
                  style={{ 
                    backgroundColor: 'rgba(30, 27, 75, 0.02)',
                    border: '1px solid rgba(30, 27, 75, 0.05)'
                  }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#1e1b4b] text-[18px]">{deal.title}</h3>
                      {deal.description && (
                        <p className="text-[14px] mt-1 line-clamp-2" style={{ color: 'rgba(30, 27, 75, 0.6)' }}>{deal.description}</p>
                      )}
                    </div>
                    {/* Status Badge - V5 compliant colors */}
                    {deal.is_paused ? (
                      <span 
                        className="px-3 py-1 text-[12px] font-bold rounded-full"
                        style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}
                      >
                        Paused
                      </span>
                    ) : deal.archived ? (
                      <span 
                        className="px-3 py-1 text-[12px] font-bold rounded-full"
                        style={{ backgroundColor: 'rgba(30, 27, 75, 0.05)', color: 'rgba(30, 27, 75, 0.5)' }}
                      >
                        Archived
                      </span>
                    ) : (
                      <span 
                        className="px-3 py-1 text-[12px] font-bold rounded-full flex items-center gap-1"
                        style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}
                      >
                        <Check className="w-3 h-3" /> Active
                      </span>
                    )}
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-4 text-[14px] mb-4" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" /> {deal.total_views ?? 0} views
                    </span>
                    <span className="flex items-center gap-1">
                      <Gift className="w-4 h-4" /> {deal.total_claims || 0} claims
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" /> {deal.total_redemptions || 0} redeemed
                    </span>
                  </div>
                  
                  {/* Actions - V5 button styles */}
                  <div className="flex flex-wrap items-center gap-2 pt-3" style={{ borderTop: '1px solid rgba(30, 27, 75, 0.05)' }}>
                    {!deal.archived && (
                      <>
                        <button
                          onClick={() => handleTogglePause(deal.id, deal.is_paused)}
                          className="px-3 py-2.5 rounded-lg text-[14px] font-medium flex items-center gap-1.5 transition-all duration-200"
                          style={deal.is_paused 
                            ? { backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }
                            : { backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }
                          }
                        >
                          {deal.is_paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                          {deal.is_paused ? 'Resume' : 'Pause'}
                        </button>
                        <button
                          onClick={() => navigate(`/business/deals/edit/${deal.id}`)}
                          className="px-3 py-2.5 bg-white rounded-lg text-[14px] font-medium flex items-center gap-1.5 transition-all duration-200"
                          style={{ 
                            border: '2px solid rgba(139, 92, 246, 0.3)',
                            color: '#8b5cf6' 
                          }}
                        >
                          <Edit2 className="w-4 h-4" /> Edit
                        </button>
                        <button
                          onClick={() => setDealToArchive(deal)}
                          className="px-3 py-2.5 bg-white rounded-lg text-[14px] font-medium flex items-center gap-1.5 transition-all duration-200"
                          style={{ 
                            border: '2px solid rgba(239, 68, 68, 0.2)',
                            color: '#ef4444' 
                          }}
                        >
                          <Archive className="w-4 h-4" /> Archive
                        </button>
                      </>
                    )}
                    {deal.archived && (
                      <button
                        onClick={() => handleRestore(deal.id)}
                        className="px-3 py-2.5 rounded-lg text-[14px] font-medium flex items-center gap-1.5 transition-all duration-200"
                        style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}
                      >
                        <Play className="w-4 h-4" /> Restore
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>

        {/* Leads Section - Only show if there are leads or lead-gen deals */}
        {(leads.length > 0 || deals.some(d => d.generate_lead)) && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.52 }}
            className="bg-white rounded-xl p-6 mb-6"
            style={{ 
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #FF7A1A',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)' 
            }}
          >
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 122, 26, 0.1)' }}>
                  <UserPlus className="w-5 h-5" style={{ color: '#FF7A1A' }} />
                </div>
                <div>
                  <h2 className="text-[18px] font-semibold text-[#1e1b4b]">Your Leads</h2>
                  <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                    {leadsStats.total} total • {leadsStats.new} new
                  </p>
                </div>
              </div>
              
              {/* Filter tabs */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'new', label: 'New' },
                  { key: 'contacted', label: 'Contacted' },
                  { key: 'converted', label: 'Converted' }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setLeadsFilter(tab.key)}
                    className={`px-3 py-1.5 rounded-md text-[13px] font-medium transition-all ${
                      leadsFilter === tab.key 
                        ? 'bg-white shadow-sm' 
                        : 'hover:bg-white/50'
                    }`}
                    style={{ color: leadsFilter === tab.key ? '#FF7A1A' : 'rgba(30, 27, 75, 0.5)' }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Lead Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <div className="p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(255, 122, 26, 0.05)' }}>
                <p className="text-[20px] font-bold" style={{ color: '#FF7A1A' }}>{leadsStats.new}</p>
                <p className="text-xs font-medium" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>New</p>
              </div>
              <div className="p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(59, 130, 246, 0.05)' }}>
                <p className="text-[20px] font-bold text-blue-500">{leadsStats.contacted}</p>
                <p className="text-xs font-medium" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Contacted</p>
              </div>
              <div className="p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(34, 197, 94, 0.05)' }}>
                <p className="text-[20px] font-bold text-green-500">{leadsStats.converted}</p>
                <p className="text-xs font-medium" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Converted</p>
              </div>
              <div className="p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(107, 114, 128, 0.05)' }}>
                <p className="text-[20px] font-bold text-gray-500">{leadsStats.closed}</p>
                <p className="text-xs font-medium" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Closed</p>
              </div>
            </div>

            {/* Leads List */}
            {leadsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#FF7A1A' }} />
              </div>
            ) : leads.length === 0 ? (
              <div className="text-center py-8" style={{ color: 'rgba(30, 27, 75, 0.4)' }}>
                <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No leads yet</p>
                <p className="text-[13px] mt-1">Leads will appear here when customers claim your lead-gen deals</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {leads
                  .filter(lead => leadsFilter === 'all' || lead.status === leadsFilter)
                  .map(lead => (
                    <div 
                      key={lead.id}
                      className="p-4 rounded-xl transition-all duration-200 hover:-translate-y-0.5"
                      style={{ 
                        backgroundColor: lead.status === 'new' ? 'rgba(255, 122, 26, 0.03)' : 'rgba(30, 27, 75, 0.02)',
                        border: lead.status === 'new' ? '1px solid rgba(255, 122, 26, 0.2)' : '1px solid rgba(30, 27, 75, 0.05)'
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-[#1e1b4b]">
                              {lead.first_name || 'Unknown'} {lead.last_name || ''}
                            </h3>
                            <span 
                              className="px-2 py-0.5 rounded-full text-[11px] font-medium"
                              style={{
                                backgroundColor: 
                                  lead.status === 'new' ? 'rgba(255, 122, 26, 0.1)' :
                                  lead.status === 'contacted' ? 'rgba(59, 130, 246, 0.1)' :
                                  lead.status === 'converted' ? 'rgba(34, 197, 94, 0.1)' :
                                  'rgba(107, 114, 128, 0.1)',
                                color: 
                                  lead.status === 'new' ? '#FF7A1A' :
                                  lead.status === 'contacted' ? '#3b82f6' :
                                  lead.status === 'converted' ? '#22c55e' :
                                  '#6b7280'
                              }}
                            >
                              {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                            </span>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-4 text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.6)' }}>
                            <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-[#FF7A1A]">
                              <Mail className="w-3.5 h-3.5" />
                              {lead.email}
                            </a>
                            {lead.phone && (
                              <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-[#FF7A1A]">
                                <Phone className="w-3.5 h-3.5" />
                                {lead.phone}
                              </a>
                            )}
                          </div>
                          
                          <p className="text-[12px] mt-2" style={{ color: 'rgba(30, 27, 75, 0.4)' }}>
                            Claimed &quot;{lead.deal_title}&quot; • {new Date(lead.claimed_at).toLocaleDateString()}
                          </p>
                        </div>
                        
                        {/* Status Actions */}
                        <div className="flex items-center gap-2">
                          {lead.status === 'new' && (
                            <button
                              onClick={() => handleUpdateLeadStatus(lead.id, 'contacted')}
                              className="px-3 py-1.5 rounded-lg text-[12px] font-medium flex items-center gap-1 transition-all"
                              style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                              Mark Contacted
                            </button>
                          )}
                          {lead.status === 'contacted' && (
                            <button
                              onClick={() => handleUpdateLeadStatus(lead.id, 'converted')}
                              className="px-3 py-1.5 rounded-lg text-[12px] font-medium flex items-center gap-1 transition-all"
                              style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Mark Converted
                            </button>
                          )}
                          {(lead.status === 'new' || lead.status === 'contacted') && (
                            <button
                              onClick={() => handleUpdateLeadStatus(lead.id, 'closed')}
                              className="px-2 py-1.5 rounded-lg text-[12px] font-medium transition-all"
                              style={{ color: 'rgba(30, 27, 75, 0.4)' }}
                            >
                              Close
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Hub Locations Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="bg-white rounded-xl p-6"
          style={{ 
            border: '1px solid rgba(30, 27, 75, 0.05)',
            borderLeft: '4px solid #14b8a6',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)' 
          }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)' }}>
              <MapPin className="w-5 h-5 text-[#14b8a6]" />
            </div>
            <div>
              <h2 className="text-[18px] font-semibold text-[#1e1b4b]">Hub Locations</h2>
              <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Where your deals are displayed</p>
            </div>
          </div>
          
          <div className="space-y-3">
            {locations.length === 0 ? (
              <div className="text-center py-6" style={{ color: 'rgba(30, 27, 75, 0.4)' }}>
                No hub locations assigned yet.
              </div>
            ) : (
              locations.map((loc, index) => (
                <div 
                  key={loc.id || index}
                  className="p-4 bg-white rounded-xl flex items-center justify-between transition-all duration-200 hover:translate-x-1"
                  style={{ 
                    border: '1px solid rgba(30, 27, 75, 0.05)',
                    boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)' }}>
                      <MapPin className="w-5 h-5 text-[#14b8a6]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1e1b4b]">{loc.name}</h3>
                      <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>{loc.city}, {loc.state}</p>
                    </div>
                  </div>
                  <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.4)' }}>{loc.scans ?? 0} scans</p>
                </div>
              ))
            )}
          </div>
        </motion.div>
      </main>

      <ConfirmDialog
        open={!!dealToArchive}
        title="Archive this deal?"
        message={dealToArchive ? `"${dealToArchive.title}" stops showing at your venues. You can restore it from the Archived tab.` : ''}
        confirmLabel="Archive deal"
        busy={archiving}
        onConfirm={handleArchive}
        onCancel={() => setDealToArchive(null)}
      />
    </div>
  );
};

export default BusinessDashboard;
